import { AddressFields } from '@components/AddressFields';
import { DetailingAddressLocalizedFields } from '@components/DetailingAddressFieldsLocalized';
import { PageMeta } from '@components/page-meta';
import PageContainer from '@components/PageContainer';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { ArrowDropDown, ExpandMore as ExpandMoreIcon, Save } from '@material-ui/icons';
import { RouteComponentProps } from '@reach/router';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useApi from 'src/hooks/useApi';
import useCustomSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import { INACTIVE_SOURCES } from 'src/hooks/useSources';
import ApiService from 'src/modules/api-service';
import { DeliveryAddress, DetailingAddressDTO, SourceType, ValuationCountryCode } from 'src/modules/generated/api';
import { getLabel, sources } from 'src/modules/labels';

import type { AxiosResponse } from 'axios';

type DetailingAddressFormData = {
  [k in SourceType | 'default']?: k extends 'default'
    ? DeliveryAddress
    : {
        [u in ValuationCountryCode | 'default']?: Partial<DeliveryAddress>;
      };
};

const useStyles = makeStyles((theme) => ({
  headline: {
    marginBlockEnd: theme.spacing(4),
    hyphens: 'auto',
  },
  card: {
    padding: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
  },
}));

const dtoToFormDataTranslator = (input: DetailingAddressDTO): DetailingAddressFormData => ({
  default: input.fallback,
  ...Object.fromEntries(
    Object.entries(input.perSource || {}).map(([source, detailingAddressPerSource]) => [
      source,
      {
        default: detailingAddressPerSource.fallback,
        ...detailingAddressPerSource.perCountry,
      },
    ]),
  ),
});

const formDataToDtoTranslator = ({ default: d, ...s }: DetailingAddressFormData): DetailingAddressDTO => ({
  fallback: d!,
  // hacky workaround because of the problematic typing of the objects
  // fallback is optional but if the object fallback is set, then some values are required within
  perSource: Object.fromEntries(
    Object.entries(s).map(([source, { default: d2, ...rest }]) => {
      const entry: any = {};
      if (
        !isEmpty(d2) &&
        Object.values(d2 as any).some((value) => value !== undefined && value !== null && value !== '')
      )
        entry.fallback = d2;
      if (Object.keys(rest).length > 0) entry.perCountry = { ...rest };
      return [source, entry];
    }),
  ),
});

const DetailingAddressPage = (_props: RouteComponentProps) => {
  const classes = useStyles();
  const { fetch } = useApi<DetailingAddressDTO>();
  const form = useForm<DetailingAddressFormData>();
  const { handleSubmit, reset } = form;
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);
  const snackbar = useCustomSnackbarGlobal();

  const handleFormSubmit = handleSubmit(async (data) => {
    const payload = formDataToDtoTranslator(data);
    // delete empty sources to avoid wrong states in the database
    if (payload?.perSource) {
      const { perSource } = payload;
      Object.keys(perSource).forEach((source: string) => {
        if (isEmpty(perSource[source])) {
          delete perSource[source];
        }
      });
    }

    try {
      const response = await fetch(ApiService.config.configControllerSaveDetailingAddress(payload));
      if (response?.status === 200) {
        snackbar.showSuccess(t('alerts.successSaved'));
      } else snackbar.showError(t('snackBar.error.saveNotPossibleCheckFields'));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      snackbar.showError(t('snackBar.error.saveNotPossibleCheckFields'));
    }
  });

  useEffect(() => {
    const getInitialData = async () => {
      let res: AxiosResponse<DetailingAddressDTO> | undefined;

      try {
        res = await fetch(ApiService.config.configControllerGetDetailingAddress());
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
        throw Error(t('alerts.errorRaised'));
      }

      if (!res?.data) {
        throw Error(t('alerts.errorRaised'));
      }

      let defaultValues: DetailingAddressFormData = {};

      try {
        defaultValues = dtoToFormDataTranslator(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
        throw Error(t('alerts.errorRaised'));
      }
      return defaultValues;
    };

    getInitialData()
      .then((initialData) => reset(initialData))
      .catch((e) => {
        if (e.message) {
          snackbar.showError(e.message);
        }
      });
  }, [fetch, reset, snackbar, t]);

  return (
    <>
      <PageMeta title="navigation.detailing" />
      <PageContainer>
        <Typography variant="h1" className={classes.headline}>
          {t('navigation.detailing')}
        </Typography>

        <FormProvider {...form}>
          <form onSubmit={handleFormSubmit}>
            <Grid container spacing={4}>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12}>
                  <Card className={classes.card}>
                    <AddressFields source="default" />
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  {showMore ? (
                    Object.values(SourceType)
                      .filter((source) => !INACTIVE_SOURCES.includes(source))
                      .map((source) => (
                        <Accordion key={source}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel-${source}-content`}
                            id={`panel-${source}-header`}
                          >
                            <Typography variant="h6">{getLabel(sources, source)}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <DetailingAddressLocalizedFields source={source} />
                          </AccordionDetails>
                        </Accordion>
                      ))
                  ) : (
                    <Button
                      color="secondary"
                      variant="text"
                      type="button"
                      endIcon={<ArrowDropDown />}
                      onClick={() => setShowMore(true)}
                    >
                      {t('common.showMore')}
                    </Button>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Button color="primary" endIcon={<Save />} type="submit" variant="contained">
                  {t('common.save')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </PageContainer>
    </>
  );
};

export default DetailingAddressPage;
